import type { InvestorProfile } from '@/api/custom';
import { getSessionAssociationByThreadId } from '@/api/weave-x';
import { getThreadMessages, getThreadState } from '@/api/weave-x-agents';
import type { Thread } from '@/common/components/wavvest-planning/types';
import {
  extractHistoryMessagesFromResponse,
  extractVisualizationHtmlFromState,
  hasPersistedThreadTitle,
  isLegacyThreadMetadata,
  mapHistoryResponseToConversation,
} from '@/common/components/wavvest-planning/orchestrator-message-adapter';
import {
  extractPlannerModeFromThreadState,
  type PlannerMode,
} from '@/common/components/wavvest-planning/planner-mode';
import {
  extractPlannerEngineFromThreadState,
  type PlannerEngine,
} from '@/common/components/wavvest-planning/planner-engine';
import { mapParsedInterruptToGraphInterrupt } from '@/common/components/wavvest-planning/interrupt-adapter';
import { parseInterruptFromThreadState } from '@/common/components/wavvest-planning/planning-ws-event-parser';
import { replaceSuggestedPromptsWithNames } from '@/common/components/wavvest-planning/conversation-panel/history-message-display';
import canvasWebSocket from '@/services/websocket-service';
import type { AppDispatch } from '@/store';
import {
  resetCanvas,
  setConversation,
  setIsLegacySession,
  setPendingInterrupt,
  updateVisualization,
} from '@/store/features/canvas/canvasSlice';
import { setSelectedInvestor } from '@/store/features/planning/planningSlice';
import { getInvestorDisplayName } from '@/util/investor-display';

type RestorePlannerSessionOptions = {
  threadId: string;
  dispatch: AppDispatch;
  investors: InvestorProfile[];
  fallbackThread?: Thread;
};

export type RestorePlannerSessionResult = {
  plannerMode: PlannerMode | null;
  plannerEngine: PlannerEngine | null;
};

export async function hydrateThreadsWithHistoryTitles(
  threads: Thread[],
  maxThreadsToHydrate: number,
): Promise<Thread[]> {
  const threadIdsToHydrate = new Set(
    [...threads]
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .filter((thread) => !hasPersistedThreadTitle(thread))
      .slice(0, maxThreadsToHydrate)
      .map((thread) => thread.thread_id),
  );

  return Promise.all(
    threads.map(async (thread) => {
      if (!threadIdsToHydrate.has(thread.thread_id)) {
        return thread;
      }

      try {
        const messages = extractHistoryMessagesFromResponse(
          await getThreadMessages(thread.thread_id),
        );
        if (messages.length === 0) {
          return thread;
        }

        return {
          ...thread,
          values: {
            ...(thread.values ?? {}),
            messages,
          },
        };
      } catch (error) {
        console.warn('Failed to fetch session title messages:', error);
        return thread;
      }
    }),
  );
}

export async function restorePlannerSession({
  threadId,
  dispatch,
  investors,
  fallbackThread,
}: RestorePlannerSessionOptions): Promise<RestorePlannerSessionResult> {
  const [threadState, historyResponse, association] = await Promise.all([
    getThreadState(threadId).catch((error) => {
      console.warn('Failed to fetch full thread state, falling back to thread list state:', error);
      return null;
    }),
    getThreadMessages(threadId),
    getSessionAssociationByThreadId(threadId).catch((error) => {
      console.warn('Failed to fetch session association:', error);
      return null;
    }),
  ]);

  dispatch(resetCanvas());
  canvasWebSocket.updateSessionId(threadId);

  dispatch(
    setIsLegacySession(isLegacyThreadMetadata(threadState?.metadata ?? fallbackThread?.metadata)),
  );

  dispatch(
    setConversation({
      messages: replaceSuggestedPromptsWithNames(mapHistoryResponseToConversation(historyResponse)),
    }),
  );

  dispatch(
    updateVisualization({
      sourceCode: extractVisualizationHtmlFromState(threadState ?? fallbackThread),
    }),
  );

  const restoredInterrupt = mapParsedInterruptToGraphInterrupt(
    parseInterruptFromThreadState(threadState),
  );
  if (restoredInterrupt) {
    dispatch(setPendingInterrupt(restoredInterrupt));
  }

  if (association) {
    const investor = investors.find((candidate) => candidate.id === association.investorId);
    dispatch(
      setSelectedInvestor({
        id: association.investorId,
        cognitoId: investor?.cognito_id ?? undefined,
        name: investor ? getInvestorDisplayName(investor) : undefined,
      }),
    );
  } else {
    dispatch(setSelectedInvestor(undefined));
  }

  const restoredThreadState = threadState ?? fallbackThread;
  return {
    plannerMode: extractPlannerModeFromThreadState(restoredThreadState),
    plannerEngine: extractPlannerEngineFromThreadState(restoredThreadState),
  };
}
